import {FakturierungsbelegFormState} from '../states/fakturierungsbeleg-form.state';
import {createReducer, on} from '@ngrx/store';
import {FakturierungsbelegFormActions} from '../actions/fakturierungsbeleg-form.actions';
import {PositionMengeMode} from '../../interfaces/position-menge-modes.interface';
import {PositionEinzelpreisNettoMode} from '../../interfaces/position-einzelpreisnetto-mode.interface';
import {PositionBeschreibungMode} from '../../interfaces/position-beschreibung-mode.interface';
import {PositionDTO} from '../../openapi/fakturierung-openapi';
import {KEINE_REFERENZFELDER_SICHTBAR, referenzfelderSichtbarkeitAusBeleg} from '../../util/referenzfelder';


export const initialFakturierungsbelegFormState: FakturierungsbelegFormState = {
  fakturierungsbeleg: {},
  positionen: [],
  addPositionMode: 'display',
  kontaktMode: 'display',
  belegdatumMode: 'display',
  vorlaufzeileMode: 'display',
  leistungsLieferdatumControlMode: 'display',
  leistungsLieferdatumDateMode: 'single',
  positionMengeModes: [],
  positionEinzelpreisNettoModes: [],
  positionBeschreibungModes: [],
  positionUpdateBackups: {},
  referenzfelderSichtbar: KEINE_REFERENZFELDER_SICHTBAR,
};

export const fakturierungsbelegFormReducer = createReducer(
  initialFakturierungsbelegFormState,

  on(FakturierungsbelegFormActions.reset, () => initialFakturierungsbelegFormState),

  on(FakturierungsbelegFormActions.updateFakturierungsbelegDto, (state, {fakturierungsbelegDto}) => ({
    ...state,
    fakturierungsbeleg: fakturierungsbelegDto,
  })),

  on(FakturierungsbelegFormActions.hydrateReferenzfelder, (state, {belegDto}) => ({
    ...state,
    referenzfelderSichtbar: referenzfelderSichtbarkeitAusBeleg(belegDto),
    referenzfelderHydratedBelegId: belegDto.id,
  })),

  on(FakturierungsbelegFormActions.toggleReferenzfeld, (state, {feld, sichtbar}) => ({
    ...state,
    referenzfelderSichtbar: {
      ...state.referenzfelderSichtbar,
      [feld]: sichtbar,
    },
  })),

  on(FakturierungsbelegFormActions.resetReferenzfelderSichtbarkeit, state => ({
    ...state,
    referenzfelderSichtbar: KEINE_REFERENZFELDER_SICHTBAR,
  })),

  on(FakturierungsbelegFormActions.updateLogoUrl, (state, {objectUrl}) => {
    // INFO: Alte Logo URL freigeben
    const oldLogoUrl = state.logoUrl;
    if (oldLogoUrl) {
      URL.revokeObjectURL(oldLogoUrl);
    }

    return {
      ...state,
      logoUrl: objectUrl,
    };
  }),

  on(FakturierungsbelegFormActions.changeKontaktMode, (state, {mode}) => {
    return {
      ...state,
      kontaktMode: mode,
    };
  }),

  on(FakturierungsbelegFormActions.changeVorlaufzeileMode, (state, {mode}) => {
    return {
      ...state,
      vorlaufzeileMode: mode,
    };
  }),

  on(FakturierungsbelegFormActions.fetchPositionenSuccess, (state, {positionen}) => {
    const positionMengeModes: PositionMengeMode[] = [];
    const positionEinzelpreisNettoModes: PositionEinzelpreisNettoMode[] = [];

    for (const position of positionen ?? []) {
      positionMengeModes.push({
        nummer: position.nummer,
        mode: 'display',
      });

      positionEinzelpreisNettoModes.push({
        nummer: position.nummer,
        mode: 'display',
      });
    }

    return {
      ...state,
      positionMengeModes,
      positionEinzelpreisNettoModes: positionEinzelpreisNettoModes,
      positionBeschreibungModes: [],
      positionen: positionen ?? [],
      positionUpdateBackups: {},
    };
  }),

  on(FakturierungsbelegFormActions.addPositionSuccess, (state, {newlyCreatedPosition}) => {
    return {
      ...state,
      positionen: [...(state.positionen || []), newlyCreatedPosition],
    };
  }),

  on(FakturierungsbelegFormActions.deletePositionSuccess, (state, {positionId}) => {
    if (!state.positionen || state.positionen.length === 0) {
      throw new Error('Cannot delete Position in an empty or undefined array of positionen!');
    }
    const updatedPositionen = state.positionen.filter(position => position.id !== positionId);

    const {[positionId]: _removedBackup, ...remainingBackups} = state.positionUpdateBackups;

    return {
      ...state,
      positionen: updatedPositionen,
      positionBeschreibungModes: state.positionBeschreibungModes.filter(item => item.positionId !== positionId),
      positionUpdateBackups: remainingBackups,
    };
  }),

  on(FakturierungsbelegFormActions.updatePosition, (state, {updatePositionObj}) => {
    const {positionId, payload} = updatePositionObj;
    const existingPosition = state.positionen?.find(position => position.id === positionId);

    if (!existingPosition) {
      return state;
    }

    // INFO: betragNetto wird bewusst NICHT optimistisch aktualisiert - der korrekte Wert kommt mit updatePositionSuccess vom Server.
    const optimisticallyUpdatedPosition: PositionDTO = {
      ...existingPosition,
      menge: payload.menge ?? existingPosition.menge,
      einzelpreisNetto: payload.einzelpreisNetto ?? existingPosition.einzelpreisNetto,
      beschreibung: payload.beschreibung ?? existingPosition.beschreibung,
    };

    const updatedPositionen = state.positionen.map(position =>
      position.id === positionId ? optimisticallyUpdatedPosition : position
    );

    // INFO: Backup nur sichern, falls noch kein Backup fuer diese positionId existiert.
    // So bleibt bei schneller Folgebearbeitung der wirklich urspruengliche Wert als Revert-Ziel.
    const positionUpdateBackups = state.positionUpdateBackups[positionId]
      ? state.positionUpdateBackups
      : {...state.positionUpdateBackups, [positionId]: existingPosition};

    return {
      ...state,
      positionen: updatedPositionen,
      positionUpdateBackups,
    };
  }),

  on(FakturierungsbelegFormActions.updatePositionSuccess, (state, {newlyUpdatedPosition}) => {
    if (!state.positionen || state.positionen.length === 0) {
      throw new Error('Cannot update an empty or undefined array of positionen!');
    }

    const updatedPositionen = state.positionen.map(position =>
      position.id === newlyUpdatedPosition.id ? newlyUpdatedPosition : position
    );

    const {[newlyUpdatedPosition.id]: _removedBackup, ...remainingBackups} = state.positionUpdateBackups;

    return {
      ...state,
      positionen: updatedPositionen,
      positionUpdateBackups: remainingBackups,
    };
  }),

  on(FakturierungsbelegFormActions.updatePositionFailure, (state, {positionId}) => {
    const backup = state.positionUpdateBackups[positionId];

    if (!backup) {
      return state;
    }

    const revertedPositionen = (state.positionen ?? []).map(position =>
      position.id === positionId ? backup : position
    );

    const {[positionId]: _removedBackup, ...remainingBackups} = state.positionUpdateBackups;

    return {
      ...state,
      positionen: revertedPositionen,
      positionUpdateBackups: remainingBackups,
    };
  }),

  on(FakturierungsbelegFormActions.changePositionMengeMode, (state, {positionMengeMode}) => {
    const positionMengeModes = [
      ...state.positionMengeModes || [],
    ];

    const positionMengeModeIndex = positionMengeModes.findIndex(item => {
      return item.nummer === positionMengeMode.nummer;
    });

    if (positionMengeModeIndex != undefined && positionMengeModeIndex !== -1) {
      positionMengeModes.splice(positionMengeModeIndex, 1);
    }

    positionMengeModes.push({
      nummer: positionMengeMode.nummer,
      mode: positionMengeMode.mode,
    });

    return {
      ...state,
      positionMengeModes,
    };
  }),

  on(FakturierungsbelegFormActions.changePositionEinzelpreisNettoMode, (state, {positionEinzelpreisNettoMode: positionEinzelpreisNettoMode}) => {
    const positionEinzelpreisNettoModes = [
      ...state.positionEinzelpreisNettoModes || [],
    ];

    const positionEinzelpreisNettoModeIndex = positionEinzelpreisNettoModes.findIndex(item => {
      return item.nummer === positionEinzelpreisNettoMode.nummer;
    });
    if (positionEinzelpreisNettoModeIndex != undefined && positionEinzelpreisNettoModeIndex !== -1) {
      positionEinzelpreisNettoModes.splice(positionEinzelpreisNettoModeIndex, 1);
    }

    positionEinzelpreisNettoModes.push({
      nummer: positionEinzelpreisNettoMode.nummer,
      mode: positionEinzelpreisNettoMode.mode,
    });

    return {
      ...state,
      positionEinzelpreisNettoModes: positionEinzelpreisNettoModes,
    };
  }),

  on(FakturierungsbelegFormActions.changePositionBeschreibungMode, (state, {positionBeschreibungMode}) => {
    const others = state.positionBeschreibungModes.filter(item => item.positionId !== positionBeschreibungMode.positionId);
    const positionBeschreibungModes: PositionBeschreibungMode[] = [
      ...others,
      {
        positionId: positionBeschreibungMode.positionId,
        mode: positionBeschreibungMode.mode,
      },
    ];

    return {
      ...state,
      positionBeschreibungModes,
    };
  }),

  on(FakturierungsbelegFormActions.changeAddPositionMode, (state, {mode}) => {
    return {
      ...state,
      addPositionMode: mode,
    };
  }),

  on(FakturierungsbelegFormActions.changeBelegdatumMode, (state, {mode}) => {
    return {
      ...state,
      belegdatumMode: mode,
    };
  }),

  on(FakturierungsbelegFormActions.changeLeistungsLieferDatumControlMode, (state, {mode}) => {
    return {
      ...state,
      leistungsLieferdatumControlMode: mode,
    };
  }),

  on(FakturierungsbelegFormActions.changeLeistungsLieferDatumDateMode, (state, {mode}) => {
    return {
      ...state,
      leistungsLieferdatumDateMode: mode,
    };
  }),
);
